import { useEffect, useRef } from 'react';
import type { MutableRefObject } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Formik, useFormikContext } from 'formik';
import { array as yupArray, object as yupObject, string as yupString } from 'yup';
import { CardHeader, Grid, Heading, Box } from '@gusto/workbench';
import { Form, SelectableCard, SelectableCardGroup } from '@gusto/workbench-formik';
import Button from 'components/button';
import { trackError } from 'helpers/track_error';
import { usePartnerSignupContext } from './partnersSignupProvider';
import type { PartnerQuestionType } from './types';
import { SignUpGoalsValues, PerkGoalsValues, YesNoValues } from './types';

const scrollToQuestion = (ref: MutableRefObject<HTMLFieldSetElement | null>) => {
  if (ref?.current?.scrollIntoView) {
    try {
      ref?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    } catch (e) {
      trackError({
        error: new Error(`Error within signup one_page scrollToQuestion`),
      });
    }
  }
};

const FormObserver = ({
  signupGoalsRef,
  runPayrollForClientsRef,
  offerAdvisoryServicesRef,
  perkGoalsRef,
  submitRef,
}: {
  signupGoalsRef: MutableRefObject<HTMLFieldSetElement | null>;
  runPayrollForClientsRef: MutableRefObject<HTMLFieldSetElement | null>;
  offerAdvisoryServicesRef: MutableRefObject<HTMLFieldSetElement | null>;
  perkGoalsRef: MutableRefObject<HTMLFieldSetElement | null>;
  submitRef: MutableRefObject<HTMLFieldSetElement | null>;
}) => {
  const { values } = useFormikContext<PartnerQuestionType>();
  const {
    setSignUpGoalsComplete,
    setRunPayrollForClientsComplete,
    setOfferAdvisoryServicesComplete,
    setPerkGoalsComplete,
    signupGoals,
    runPayrollForClients,
    offerAdvisoryServices,
    perkGoals,
  } = usePartnerSignupContext();

  const scrollToNextQuestion = () => {
    if (values.signupGoals && !values.runPayrollForClients) {
      scrollToQuestion(runPayrollForClientsRef);
    } else if (values.signupGoals && values.runPayrollForClients && !values.offerAdvisoryServices) {
      scrollToQuestion(offerAdvisoryServicesRef);
    } else if (values.signupGoals && values.runPayrollForClients && values.offerAdvisoryServices) {
      if (!values.perkGoals) {
        scrollToQuestion(perkGoalsRef);
      } else {
        scrollToQuestion(submitRef);
      }
    } else if (
      !values.signupGoals &&
      (values.runPayrollForClients || values.offerAdvisoryServices || values.perkGoals)
    ) {
      scrollToQuestion(signupGoalsRef);
    }
  };

  useEffect(() => {
    if (values.signupGoals && values.signupGoals !== signupGoals) {
      setSignUpGoalsComplete(values.signupGoals);
    } else if (
      values.runPayrollForClients &&
      values.runPayrollForClients !== runPayrollForClients
    ) {
      scrollToNextQuestion();
      setRunPayrollForClientsComplete(values.runPayrollForClients);
    } else if (
      values.offerAdvisoryServices &&
      values.offerAdvisoryServices !== offerAdvisoryServices
    ) {
      scrollToNextQuestion();
      setOfferAdvisoryServicesComplete(values.offerAdvisoryServices);
    } else if (values.perkGoals && values.perkGoals !== perkGoals) {
      scrollToNextQuestion();
      setPerkGoalsComplete(values.perkGoals);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  return null;
};

const PartnerSurvey = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const signupGoalsRef = useRef(null);
  const runPayrollForClientsRef = useRef(null);
  const offerAdvisoryServicesRef = useRef(null);
  const perkGoalsRef = useRef(null);
  const submitRef = useRef(null);

  const handleSubmit = () => {
    GustoAnalytics.track({
      eventName: window.SNOWPLOW_EVENT_CATEGORIES.VISITOR_EVENT,
      eventCategory: 'PartnerSignupSurveySubmitClicked',
    });
    // get the search params from the current URL and append them to
    // the destination path so that they persist after the form is submitted
    const destinationRoute = searchParams.size > 0 ? `/form?${searchParams.toString()}` : '/form';
    navigate(destinationRoute);
  };
  const { signupGoals, runPayrollForClients, offerAdvisoryServices, perkGoals } =
    usePartnerSignupContext();

  return (
    <Formik
      initialValues={{
        signupGoals,
        runPayrollForClients,
        offerAdvisoryServices,
        perkGoals,
      }}
      validationSchema={yupObject().shape({
        signupGoals: yupArray().of(yupString()).required('Please make a selection'),
        runPayrollForClients: yupString()
          .oneOf(['true', 'false', ''])
          .required('Please make a selection'),
        offerAdvisoryServices: yupString()
          .oneOf(['true', 'false', ''])
          .required('Please make a selection'),
        perkGoals: yupArray().of(yupString()),
      })}
      onSubmit={handleSubmit}
    >
      <Form>
        <FormObserver
          signupGoalsRef={signupGoalsRef}
          runPayrollForClientsRef={runPayrollForClientsRef}
          offerAdvisoryServicesRef={offerAdvisoryServicesRef}
          perkGoalsRef={perkGoalsRef}
          submitRef={submitRef}
        />
        <Grid maxWidth={632} autoRows="max-content" rowGap={12}>
          <SelectableCardGroup
            ref={signupGoalsRef}
            className="question"
            name="signupGoals"
            label="Tell us a little bit about yourself. Are you looking to..."
            helperText="(Select one or more)"
          >
            <Grid rowGap={3} columnGap={2} maxWidth={632} maxWidthMd="100%">
              <SelectableCard
                type="checkbox"
                aria-label="Add new client"
                value={SignUpGoalsValues.ADD_NEW_CLIENT}
              >
                <CardHeader>
                  <Heading className="answer" level={3}>
                    Add a new client to Gusto
                  </Heading>
                </CardHeader>
              </SelectableCard>
              <SelectableCard
                type="checkbox"
                aria-label="Become people advisory certified"
                value={SignUpGoalsValues.BECOME_PEOPLE_ADVISORY_CERTIFIED}
              >
                <CardHeader>
                  <Heading className="answer" level={3}>
                    Become People Advisory certified
                  </Heading>
                </CardHeader>
              </SelectableCard>
              <SelectableCard
                type="checkbox"
                aria-label="Access exisiting client"
                value={SignUpGoalsValues.ACCESS_EXISTING_CLIENT}
              >
                <CardHeader>
                  <Heading className="answer" level={3}>
                    Access a client account already on Gusto
                  </Heading>
                </CardHeader>
              </SelectableCard>
            </Grid>
          </SelectableCardGroup>

          <SelectableCardGroup
            ref={runPayrollForClientsRef}
            className="question"
            name="runPayrollForClients"
            label="Do you manage payroll for your clients?"
          >
            <Grid rowGap={3} columnGap={2} maxWidth={632} maxWidthMd="100%">
              <SelectableCard type="radio" aria-label="Yes" value={YesNoValues.YES}>
                <CardHeader>
                  <Heading className="answer" level={3}>
                    Yes
                  </Heading>
                </CardHeader>
              </SelectableCard>
              <SelectableCard type="radio" aria-label="No" value={YesNoValues.NO}>
                <CardHeader>
                  <Heading className="answer" level={3}>
                    No
                  </Heading>
                </CardHeader>
              </SelectableCard>
            </Grid>
          </SelectableCardGroup>

          <SelectableCardGroup
            ref={offerAdvisoryServicesRef}
            className="question"
            name="offerAdvisoryServices"
            label="Do you currently, or are you planning to offer advisory services to your clients?"
          >
            <Grid rowGap={3} columnGap={2} maxWidth={632} maxWidthMd="100%">
              <SelectableCard type="radio" aria-label="Yes" value={YesNoValues.YES}>
                <CardHeader>
                  <Heading className="answer" level={3}>
                    Yes
                  </Heading>
                </CardHeader>
              </SelectableCard>
              <SelectableCard type="radio" aria-label="No" value={YesNoValues.NO}>
                <CardHeader>
                  <Heading className="answer" level={3}>
                    No
                  </Heading>
                </CardHeader>
              </SelectableCard>
            </Grid>
          </SelectableCardGroup>

          <SelectableCardGroup
            ref={perkGoalsRef}
            className="question"
            name="perkGoals"
            label="Which of the following free program perks do you plan on taking advantage of?"
            helperText="(Select one or more - optional)"
          >
            <Grid rowGap={3} columnGap={2} maxWidth={632} maxWidthMd="100%">
              <SelectableCard
                type="checkbox"
                aria-label="Free payroll"
                value={PerkGoalsValues.FREE_PAYROLL}
              >
                <CardHeader>
                  <Heading className="answer" level={3}>
                    Free payroll for your firm
                  </Heading>
                </CardHeader>
              </SelectableCard>
              <SelectableCard
                type="checkbox"
                aria-label="Partner directory feature"
                value={PerkGoalsValues.PD_FEATURE}
              >
                <CardHeader>
                  <Heading className="answer" level={3}>
                    Being featured in the Gusto Partner Directory
                  </Heading>
                </CardHeader>
              </SelectableCard>
              <SelectableCard
                type="checkbox"
                aria-label="Revenue share or discounts"
                value={PerkGoalsValues.REV_SHARE_OR_DISCOUNTS}
              >
                <CardHeader>
                  <Heading className="answer" level={3}>
                    Revenue share or discounts for your clients
                  </Heading>
                </CardHeader>
              </SelectableCard>

              <SelectableCard
                type="checkbox"
                aria-label="People advisory certification"
                value={PerkGoalsValues.PEOPLE_ADVISORY_CERTIFICATION}
              >
                <CardHeader>
                  <Heading className="answer" level={3}>
                    Become People Advisory certified
                  </Heading>
                </CardHeader>
              </SelectableCard>
            </Grid>
          </SelectableCardGroup>

          <Box textAlign="center" ref={submitRef}>
            <Button
              className="btn margin-top-10px btn-primary margin-left-none full-width margin-auto btn-submit-tofu"
              type="submit"
              label="Submit"
            />
          </Box>
        </Grid>
      </Form>
    </Formik>
  );
};

export default PartnerSurvey;
